import { getAccessToken, getUser, isSupabaseAuthConfigured, refreshSession } from './supabaseAuth';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

async function apiUpsert(path, body) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation,resolution=merge-duplicates'
      },
      body: JSON.stringify(body)
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'Progress save failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    // If token expired, refresh and retry once.
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

async function apiRpc(path, body) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body || {})
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'RPC call failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

async function apiGetAuth(path) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}${path}`, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`
      }
    });

    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || 'Authenticated GET failed');
      err.status = response.status;
      throw err;
    }
    return text ? JSON.parse(text) : null;
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

export async function upsertLessonProgress({ lessonDbId, currentStep, completed }) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;
  if (!lessonDbId) return null;

  // Explicit on_conflict avoids 409s if PostgREST can't infer merge keys reliably.
  return apiUpsert('/rest/v1/user_lesson_progress?on_conflict=user_id,lesson_id', [
    {
      user_id: user.id,
      lesson_id: lessonDbId,
      current_step: currentStep,
      completed: Boolean(completed),
      completed_at: completed ? new Date().toISOString() : null,
      updated_at: new Date().toISOString()
    }
  ]);
}

export async function upsertLearningPathState({ currentModuleId, currentLessonId }) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;

  return apiUpsert('/rest/v1/user_learning_path_state?on_conflict=user_id', [
    {
      user_id: user.id,
      current_module_id: currentModuleId || null,
      current_lesson_id: currentLessonId || null,
      last_activity_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ]);
}

export async function insertAssessmentResult({ lessonId, assessmentKind, score, passed }) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;
  if (!lessonId) return null;
  if (!assessmentKind) return null;

  return apiUpsert('/rest/v1/user_assessment_results', [
    {
      user_id: user.id,
      lesson_id: lessonId,
      assessment_kind: assessmentKind,
      score: Number.isFinite(Number(score)) ? Number(score) : null,
      passed: Boolean(passed),
      attempted_at: new Date().toISOString()
    }
  ]);
}

export async function canAccessLesson({ userId, lessonId }) {
  if (!isSupabaseAuthConfigured()) return { canAccess: true, blockers: [] };
  const user = getUser();
  if (!user?.id || !userId || !lessonId) return { canAccess: true, blockers: [] };

  const rows = await apiRpc('/rest/v1/rpc/can_access_lesson', {
    p_user_id: userId,
    p_lesson_id: lessonId
  });

  const first = Array.isArray(rows) ? rows[0] : rows;
  return {
    canAccess: Boolean(first?.can_access),
    blockers: Array.isArray(first?.blockers) ? first.blockers : []
  };
}

export async function hasAtLeastOneCompletedLesson() {
  if (!isSupabaseAuthConfigured()) return false;
  const user = getUser();
  if (!user?.id) return false;

  try {
    const rows = await apiGetAuth(
      '/rest/v1/user_lesson_progress?select=lesson_id&completed=eq.true&limit=1'
    );
    return Array.isArray(rows) && rows.length > 0;
  } catch {
    return false;
  }
}
